import { BehaviorSubject, Subscription } from 'rxjs';
import { depth$, height$, width$ } from '../lib/state';
import { DimType } from '../lib/types';
import Logger from '../../js/lib/logger';
import { createDimensionalOptions } from '../lib/util.functions';
import { Measurement } from '../lib/constants';

const dimState = {
  depth: depth$,
  height: height$,
  width: width$
}

class DimensionalSelect extends HTMLElement {
  #NAME = 'DimensionalSelect';
  #abortController: AbortController | null = null;
  #form: HTMLFormElement | null;
  #wholeSelect: HTMLSelectElement | null;
  #fractionSelect: HTMLSelectElement | null;
  #type?: DimType;
  #subject?: BehaviorSubject<number>;
  #sub?: Subscription;

  constructor() {
    super();
    this.innerHTML = `
      <form>
        <div control-wrapper>
          <label for="whole"></label>
          <div select-wrapper>
            <select id="whole" name="whole">
            </select>
            <svg-icon icon="chevron" size="medium"></svg-icon>
          </div>
        </div>

        <div control-wrapper>
          <label for="fraction">Fraction</label>
          <div select-wrapper>
            <select id="fraction" name="fraction">
            </select>
            <svg-icon icon="chevron" size="medium"></svg-icon>
          </div>
        </div>
      <form>
    `;
    this.#form = this.querySelector('form');
    this.#wholeSelect = this.querySelector('form select[name="whole"]');
    this.#fractionSelect = this.querySelector('form select[name="fraction"]');
  }

  connectedCallback(): void {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    //
    this.#form?.addEventListener('change', () => this.#onFormChange(), { signal });
    //
    if (this.#type) {
      this.#wholeSelect?.append(...createDimensionalOptions(this.#type, 'whole'));
      this.#fractionSelect?.append(...createDimensionalOptions(this.#type, 'fraction'));
      this.#subject = dimState[this.#type];
      this.#sub = this.#subject.subscribe((val) =>this.#onSubject(val));
    }
  }

  disconnectedCallback(): void {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#sub?.unsubscribe();
  }

  static get observedAttributes(): string[] {
    return ['type'];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'type') {
      switch (newValue) {
        case 'width':
          this.#type = newValue;
          if (this.#wholeSelect?.parentElement?.previousElementSibling) {
            this.#wholeSelect.parentElement.previousElementSibling.textContent = 'Width';
          } 
          break;

        case 'height':
          this.#type = newValue;
          if (this.#wholeSelect?.parentElement?.previousElementSibling) {
            this.#wholeSelect.parentElement.previousElementSibling.textContent = 'Height';
          }
          this.#fractionSelect?.parentElement?.parentElement?.remove();
          this.#fractionSelect?.remove();
          this.#fractionSelect = null;
          break;

        case 'depth':
          this.#type = newValue;
          if (this.#wholeSelect?.parentElement?.previousElementSibling) {
            this.#wholeSelect.parentElement.previousElementSibling.textContent = 'Depth';
          }
          break;
      
        default:
          break;
      }
    }
  }

  #onSubject(val: number) {
    Logger.info(`${this.#NAME} ${this.#type} > #onSubject > value ${val}`);
    const valArr = val.toString().split('.');

    if (this.#wholeSelect) {
      this.#wholeSelect.value = valArr[0];
    }

    if (this.#fractionSelect && valArr[1]) {
      this.#fractionSelect.value = `0.${valArr[1]}`;
      this.#fractionSelect.dispatchEvent(new Event('change'))
    }
  }

  #onFormChange() {
    Logger.info(`${this.#NAME} ${this.#type} > #onFormChange > value ${this.#wholeSelect?.value} ${this.#fractionSelect?.value}`);
    if (this.#wholeSelect?.value) {
      let value = parseInt(this.#wholeSelect.value);
      if (value && this.#fractionSelect?.value) {
        value += parseFloat(this.#fractionSelect.value);
        if(this.#type === 'width' && value > Measurement.MAX_WIDTH) {
          value = Measurement.MAX_WIDTH;
          this.#fractionSelect.value = '0.0';
        } else if (this.#type === 'depth' && value > Measurement.MAX_DEPTH) {
          this.#fractionSelect.value = '0.0';
          value = Measurement.MAX_DEPTH;
        }
      }
      if (value) this.#subject?.next(value);
    }
    
  }
}

customElements.define('dimensional-select', DimensionalSelect);